import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Clock } from 'lucide-react';
import { remoteApi } from '../utils/api';

const STORAGE_KEY_PREFIX = 'mytime_reminder_dismissed_';

/**
 * Reminder shown when the user lands on the portal on a day that has an
 * approved telework session — suggests clocking-in on My Time (Sage).
 * Dismissal is per-day via sessionStorage so it reappears next morning.
 */
export function MyTimeReminderDialog() {
  const [open, setOpen] = useState(false);
  const [todayPeriod, setTodayPeriod] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    const today = new Date().toISOString().split('T')[0];
    const dismissed = sessionStorage.getItem(STORAGE_KEY_PREFIX + today) === '1';
    if (dismissed) return;

    remoteApi.list({}).then(res => {
      if (!mounted) return;
      const todayApproved = (res.data || []).find(
        r => r.date === today && r.status === 'approved'
      );
      if (todayApproved) {
        setTodayPeriod(todayApproved.period_type);
        setOpen(true);
      }
    }).catch(() => {});

    return () => { mounted = false; };
  }, []);

  const dismissToday = () => {
    const today = new Date().toISOString().split('T')[0];
    sessionStorage.setItem(STORAGE_KEY_PREFIX + today, '1');
    setOpen(false);
  };

  const periodLabel = todayPeriod === 'morning'
    ? 'mañana (08:00 - 14:00)'
    : todayPeriod === 'afternoon'
      ? 'tarde (14:00 - 18:00)'
      : 'jornada completa (08:00 - 18:00)';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="mytime-reminder-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <DialogTitle className="text-base">Recuerda fichar en My Time</DialogTitle>
          </div>
        </DialogHeader>
        <div className="text-sm text-slate-600 dark:text-slate-300 space-y-2">
          <p>
            Tienes <strong>teletrabajo aprobado</strong> para hoy ({periodLabel}).
          </p>
          <p>
            No olvides registrar tu jornada en <strong>My Time (Sage)</strong> además
            del fichaje en Valoriza HR.
          </p>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={dismissToday}
            data-testid="mytime-reminder-dismiss"
          >
            No mostrar más hoy
          </Button>
          <Button
            onClick={() => { setOpen(false); navigate('/clock'); }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="mytime-reminder-open"
          >
            Ir a fichar <Clock className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
